import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ButtonDeleteIcon } from "@ippon/shared-assets";
import { useForm, type UseFormRegister, type FieldErrors } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CustomButton,
  CustomTextInput,
  colors,
  CustomModal,
  FixedActionBar,
  CustomFormNavigationGuard,
} from "@ippon/shared-components";
import type { AppDispatch, RootState } from "@/store";
import {
  upsertDocumentManagement,
  type PropertyDocumentDefinition,
} from "@/store/documentManagementSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";

/**
 * 書類設定ページ「物件書類」タブ。
 * 物件詳細の「書類 > その他」枠を **クライアント単位（全物件共通）** で定義する。
 * ラベル(枠)のみここで管理し、実ファイルは物件ごとにアップロードする。
 * UI はカスタムフォーム設定を踏襲（並び替え可能なリスト＋下部まとめ保存）。
 */

// 縦方向のみに動きを制限
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

type Item = { document_type: string };

interface SortableDocRowProps {
  item: Item;
  register: UseFormRegister<Record<string, string>>;
  errors: FieldErrors<Record<string, string>>;
  onDelete: () => void;
}

const SortableDocRow: React.FC<SortableDocRowProps> = ({
  item,
  register,
  errors,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.document_type });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        p: 1,
        backgroundColor: "#FAFAFA",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#999",
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            flexShrink: 0,
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTextInput
            label=""
            name={`${item.document_type}_label`}
            register={register}
            errors={errors}
            placeholder="書類名（例: 管理委託契約書）"
            hasSpacing={false}
            inputWidth="100%"
            inputWidthSp="100%"
          />
        </Box>
        <Can action="master:delete">
          <IconButton
            size="small"
            onClick={onDelete}
            title="削除"
            sx={{
              color: colors.text.secondary,
              flexShrink: 0,
              "&:hover": { color: colors.status.error },
            }}
          >
            <ButtonDeleteIcon width={14} height={16} />
          </IconButton>
        </Can>
      </Stack>
    </Box>
  );
};

const PropertyDocumentSection: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const record = useSelector((s: RootState) => s.documentManagement.record);

  const [items, setItems] = useState<Item[]>([]);
  const [saving, setSaving] = useState(false);
  // 保存(更新)時、既存枠の削除がある場合の確認モーダル (カスタムフォームと同じ挙動)。
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const editForm = useForm<Record<string, string>>({ defaultValues: {} });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // 全入力値を購読（ラベル編集で dirty 判定を再評価させるため）
  const watched = editForm.watch();

  // レコード → ローカル state へ同期
  useEffect(() => {
    const defs = [...(record?.property_other_documents ?? [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    setItems(defs.map((d) => ({ document_type: d.document_type })));
    const values: Record<string, string> = {};
    defs.forEach((d) => {
      values[`${d.document_type}_label`] = d.display_name;
    });
    editForm.reset(values);
  }, [record, editForm]);

  // 現在の編集内容 → 保存ペイロード形
  const currentPayload: PropertyDocumentDefinition[] = useMemo(
    () =>
      items.map((it, idx) => ({
        document_type: it.document_type,
        display_name: (watched[`${it.document_type}_label`] ?? "").trim(),
        order: idx + 1,
      })),
    [items, watched],
  );

  // レコードの正規化形（差分比較用）
  const originalPayload: PropertyDocumentDefinition[] = useMemo(
    () =>
      [...(record?.property_other_documents ?? [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((d, idx) => ({
          document_type: d.document_type,
          display_name: (d.display_name ?? "").trim(),
          order: idx + 1,
        })),
    [record],
  );

  const dirty = useMemo(
    () => JSON.stringify(currentPayload) !== JSON.stringify(originalPayload),
    [currentPayload, originalPayload],
  );

  const handleAdd = () => {
    const id = `prop-${Date.now()}`;
    setItems((prev) => [...prev, { document_type: id }]);
    editForm.setValue(`${id}_label`, "");
  };

  const handleDelete = (dt: string) => {
    setItems((prev) => prev.filter((it) => it.document_type !== dt));
  };

  // 指定順で property_other_documents をまとめて PUT (並び替えの即時保存用)
  const persistOrder = async (orderedItems: Item[]) => {
    const payload: PropertyDocumentDefinition[] = orderedItems
      .map((it, idx) => ({
        document_type: it.document_type,
        display_name: (editForm.getValues(`${it.document_type}_label`) ?? "").trim(),
        order: idx + 1,
      }))
      .filter((d) => d.display_name.length > 0)
      .map((d, idx) => ({ ...d, order: idx + 1 }));
    const res = await dispatch(
      upsertDocumentManagement({
        existingId: record?.document_management_id,
        documents: record?.documents ?? { individual: [], corporate: [] },
        property_other_documents: payload,
      }) as any,
    );
    if (!upsertDocumentManagement.fulfilled.match(res)) {
      showToast({ type: "error", message: "並び替えの保存に失敗しました" });
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((it) => it.document_type === active.id);
    const to = items.findIndex((it) => it.document_type === over.id);
    if (from === -1 || to === -1) return;
    const reordered = arrayMove(items, from, to);
    setItems(reordered); // 楽観 UI
    // ラベル未入力(編集途中)の行があれば即保存しない → 「保存」ボタンに委ねる
    // (CustomForm の未保存 draft スキップと同様)
    const hasEmpty = reordered.some(
      (it) => !(editForm.getValues(`${it.document_type}_label`) ?? "").trim(),
    );
    if (hasEmpty) return;
    void persistOrder(reordered);
  };

  // ラベル未入力の枠は除外して連番を振り直す
  const buildPayload = () =>
    currentPayload
      .filter((d) => d.display_name.length > 0)
      .map((d, idx) => ({ ...d, order: idx + 1 }));

  const doSave = async () => {
    const payload = buildPayload();
    setSaving(true);
    const res = await dispatch(
      upsertDocumentManagement({
        existingId: record?.document_management_id,
        documents: record?.documents ?? { individual: [], corporate: [] },
        property_other_documents: payload,
      }) as any,
    );
    setSaving(false);
    if (upsertDocumentManagement.fulfilled.match(res)) {
      showToast({ type: "success", message: "物件書類を更新しました" });
    } else {
      showToast({ type: "error", message: "物件書類の更新に失敗しました" });
    }
  };

  // 更新ボタン: 既存枠の削除がある場合は確認モーダルを挟む (カスタムフォームと同じ)。
  const handleSave = () => {
    const payload = buildPayload();
    const payloadKeys = new Set(payload.map((d) => d.document_type));
    const deletedCount = originalPayload.filter(
      (d) => !payloadKeys.has(d.document_type),
    ).length;
    if (deletedCount > 0) {
      setDeleteConfirmOpen(true);
      return;
    }
    void doSave();
  };

  return (
    <CustomFormNavigationGuard hasFormChanged={dirty}>
      {() => (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          mt: 1,
          color: "#666",
          fontSize: { xs: 12, sm: 14 },
          lineHeight: 1.6,
        }}
      >
        物件の書類に表示する項目を作成してください。全物件共通で任意に設定できます。
      </Typography>

      <Box sx={{ width: { xs: "100%", md: "60%" } }}>
        {items.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((it) => it.document_type)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} useFlexGap sx={{ mb: 1.5 }}>
                {items.map((it) => (
                  <SortableDocRow
                    key={it.document_type}
                    item={it}
                    register={editForm.register}
                    errors={editForm.formState.errors}
                    onDelete={() => handleDelete(it.document_type)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}

        <Can action="master:edit">
          <CustomButton
            label="新しい項目を追加"
            buttonCategory="clearPrimary"
            onClick={handleAdd}
            sx={{ width: "100%", borderStyle: "dashed" }}
          />
        </Can>
      </Box>

      <FixedActionBar
        submitLabel="更新"
        isLoading={saving}
        disabled={!dirty || saving}
        onSubmit={() => handleSave()}
      />

      {/* 削除確認モーダル (カスタムフォームと同じ。更新時に既存枠の削除があれば出す) */}
      <CustomModal
        openModal={deleteConfirmOpen}
        handleCloseModal={() => setDeleteConfirmOpen(false)}
        title="削除の確認"
        modalType="deleteModal"
        bodyText=""
        hideCloseButton
      >
        <Box>
          <Typography sx={{ fontSize: 14, color: "#666", mb: 2 }}>
            削除すると、全ての物件の書類でこの項目の入力内容が削除されます。
            <br />
            この操作は取り消せません。削除してよろしいですか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={() => setDeleteConfirmOpen(false)}
            buttonCategory="cancelModal"
          />
          <Can action="master:delete">
            <CustomButton
              label="削除する"
              onClick={() => {
                setDeleteConfirmOpen(false);
                void doSave();
              }}
              buttonCategory="deleteModal"
            />
          </Can>
        </Box>
      </CustomModal>
    </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default PropertyDocumentSection;
